'use memo';

import type { CSSProperties } from 'react';
import {
  addSpaceBetweenCamelCase,
  AgxDiagonalUpArrowIcon,
  AgxBadge,
  AgxBadgeType,
  cleanFullAddress,
  ContractDetailModel,
  NswContractStage,
  PropertyAddress,
  QldContractStage,
} from '@urbanx/agx-ui-components';
import {
  AgxAvatarLabelGroup,
  AgxBadge as AgxBadgeV2,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2';
import { useGetUrbanXTeamForAgency, useStaffGetAgentsInAgency } from '@urbanx/agx.agencies.client';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { useParams } from 'react-router-dom';
import { useSelectedAgency, useSelectedCampaign } from 'hooks';
import { getContractStageBadgeType } from 'pages/contracts/contractStageBadge';
import contractHeaderIcon from 'assets/images/contract-header-icon.svg';
import rexConnectionDotInner from 'assets/images/rex/rex-connection-dot-inner.svg';
import rexConnectionDotOuter from 'assets/images/rex/rex-connection-dot-outer.svg';

interface ContractSectionHeaderProps {
  contract: ContractDetailModel;
}

const ContractSectionHeader = ({ contract }: ContractSectionHeaderProps) => {
  const { agencyId } = useParams();
  const { agency } = useSelectedAgency();
  const { campaign } = useSelectedCampaign();
  const { data: agents } = useStaffGetAgentsInAgency(agencyId ?? '', !!agencyId);
  const { data: urbanXTeamForAgency } = useGetUrbanXTeamForAgency(agencyId ?? '', !!agencyId);
  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaign?.campaignId ?? '',
    !!campaign?.campaignId
  );
  const contractStage = addSpaceBetweenCamelCase(contract.contractStage);
  const rexListingLink =
    rexListing?.listingId && rexListing.brandAccountId
      ? `https://app.rexsoftware.com/listings/#id=${encodeURIComponent(
          rexListing.listingId
        )}&_account_id=${encodeURIComponent(rexListing.brandAccountId)}`
      : undefined;
  const listingAgents = agents?.filter(
    (agent) =>
      agent.id === campaign?.leadAgentId ||
      (agent.id === campaign?.secondAgentId && agent.softDeleted === false)
  );

  return (
    <section className="contractSectionHeader">
      <AgxSpacer direction="vertical" gap="spacing-lg" style={{ width: '100%' }}>
        <AgxSpacer direction="horizontal" contentAlignment="start" gap="spacing-lg">
          <AgxSpacer
            className="contractSectionHeaderIllustration"
            direction="horizontal"
            contentAlignment="center"
            contentJustification="center"
            flexShrink={0}
          >
            <img src={contractHeaderIcon} alt="" />
          </AgxSpacer>

          <AgxSpacer direction="vertical" gap="2xs" fill>
            {campaign?.address && (
              <AgxTypography
                id="contract-address"
                type="body_ui"
                size="small"
                color="var(--text-tertiary)"
                style={{ paddingTop: 'var(--spacing-xs)' }}
              >
                {cleanFullAddress(campaign.address as PropertyAddress)}
              </AgxTypography>
            )}

            <AgxSpacer
              className="contractSectionHeaderTitleRow"
              direction="horizontal"
              contentAlignment="center"
              gap="spacing-md"
            >
              <AgxTypography id="title" type="heading" size="x-large" color="var(--text-primary)">
                {contract.contractName}
              </AgxTypography>
              <AgxBadge
                text={contractStage}
                type={
                  contract.contractStage === QldContractStage.BackUp
                    ? AgxBadgeType.Finished
                    : getContractStageBadgeType(
                        contract.contractStage as QldContractStage | NswContractStage
                      )
                }
              />
              {!isPendingRexListing && (
                <span className="contractSectionHeaderRexBadge">
                  <AgxBadgeV2
                    type="colourfill"
                    size="md"
                    colour="lightgrey"
                    showBorder
                    text={rexListing ? `REX ${rexListing.listingId}` : 'REX disconnected'}
                    leftIcon={
                      rexListing ? (
                        <span className="rexConnectionDot" aria-label="REX connected">
                          <img src={rexConnectionDotOuter} alt="" />
                          <img src={rexConnectionDotInner} alt="" />
                        </span>
                      ) : (
                        <span className="rexConnectionDot rexConnectionDotDisconnected" />
                      )
                    }
                    link={rexListingLink}
                    rightIcon={<AgxDiagonalUpArrowIcon color="#000" />}
                  />
                </span>
              )}
            </AgxSpacer>

            <AgxSpacer className="contractSectionHeaderAvatars" direction="horizontal" gap="sm">
              <span
                className="agencyAvatarBranding"
                style={
                  {
                    '--agency-avatar-background':
                      agency?.agency?.settings?.branding?.backgroundColor,
                  } as CSSProperties
                }
              >
                <AgxAvatarLabelGroup
                  id="contract-agency-avatar"
                  type="avatar"
                  variant="round"
                  photoUrl={agency?.agency?.settings?.branding?.logo?.publicPath}
                  label1={agency?.name ?? 'Agency'}
                  mode="letters"
                  backgroundMode="dark"
                  size="small"
                />
              </span>
              {listingAgents?.map((agent) => (
                <AgxAvatarLabelGroup
                  id={`contract-agent-avatar-${agent.id}`}
                  key={agent.id}
                  type="avatar"
                  variant="round"
                  photoUrl={agent.photoUrl}
                  label1={`${agent.name?.firstName} ${agent.name?.lastName}`}
                  size="small"
                />
              ))}
              {urbanXTeamForAgency?.adminManager && (
                <AgxAvatarLabelGroup
                  id="contract-admin-avatar"
                  type="avatar"
                  variant="round"
                  photoUrl={urbanXTeamForAgency.adminManager.photo?.publicPath}
                  label1={`${urbanXTeamForAgency.adminManager.firstName} ${urbanXTeamForAgency.adminManager.lastName}`}
                  size="small"
                />
              )}
            </AgxSpacer>
          </AgxSpacer>
        </AgxSpacer>
      </AgxSpacer>
    </section>
  );
};

export default ContractSectionHeader;
